import { gql } from 'apollo-boost';
import { useFiltersFromUrl } from 'core/hooks/useFiltersFromUrl';
import { button, buttonAccent } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { isMobile } from 'core/utils';
import React, { useCallback, useMemo } from 'react';
import { useGetCourseFilterTagsQuery } from 'site/graphql';
import Select from '../../../core/components/Select';
import { complexityOptions, priceOptions } from './const';
import DropDownMobileFilter from './DropDownMobileFilter';
import DropDownTagsFilter from './DropDownTagsFilter';

gql`
    query GetCourseFilterTags($filter: TagListRequest) {
        tags(filter: $filter) {
            result {
                id
                name
                description
            }
        }
    }
`;

type ICoursesFilterProps = {
    fetch: (newFilter: object) => void;
    isFreeFilterVisible: boolean;
    complexityFilterVisible: boolean;
    tagsFilterVisible: boolean;
} & WithStyles<typeof styles>;

const CoursesFilter: React.FC<ICoursesFilterProps> = ({
    classes,
    fetch,
    complexityFilterVisible,
    isFreeFilterVisible,
    tagsFilterVisible,
}) => {
    const { isFree, tags, complexity } = useFiltersFromUrl();
    const { data } = useGetCourseFilterTagsQuery({ variables: { filter: { objectType: 'product' } } });
    const tagsData = (data && data.tags && data.tags.result) || [];

    const handleFilterChange = useCallback(
        (property: string, value: any) => {
            fetch({ [property]: value });
        },
        [fetch],
    );

    const resetFilter = useCallback(() => {
        fetch({
            complexity: undefined,
            tags: undefined,
            isFree: undefined,
        });
    }, [fetch]);

    const hasFilter = useMemo(() => {
        return typeof tags !== 'undefined' || typeof complexity !== 'undefined' || typeof isFree !== 'undefined';
    }, [tags, complexity, isFree]);

    return (
        <>
            {isMobile() ? (
                <div className={classes.mobileContainer}>
                    <DropDownMobileFilter
                        isFreeFilterVisible={isFreeFilterVisible}
                        complexityFilterVisible={complexityFilterVisible}
                        onFilterChange={fetch}
                    />
                    {tagsFilterVisible && (
                        <div className={classes.tagsFilterWrap}>
                            <DropDownTagsFilter tags={tagsData} onFilterChange={fetch} />
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {tagsFilterVisible && (
                        <div className={classes.tagsFilterWrap}>
                            <DropDownTagsFilter tags={tagsData} onFilterChange={fetch} />
                        </div>
                    )}
                    {isFreeFilterVisible && (
                        <Select
                            placeholder="Цена"
                            options={priceOptions}
                            value={isFree && isFree.toString()}
                            onChange={value => handleFilterChange('isFree', value)}
                        />
                    )}
                    {complexityFilterVisible && (
                        <Select
                            placeholder="Уровень"
                            options={complexityOptions}
                            value={complexity && complexity.toString()}
                            onChange={value => handleFilterChange('complexity', value)}
                        />
                    )}
                </>
            )}
            {hasFilter && (
                <button className={classes.button} onClick={resetFilter}>
                    Сбросить все
                </button>
            )}
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        button: {
            fontSize: 0.9 + 'rem',
            fontWeight: 600,

            [theme.breakpoints.up('sm')]: {
                ...button(theme),
                ...buttonAccent(theme),
                backgroundColor: '#cb0d26',
                padding: '10px 18px',
            },

            [theme.breakpoints.down('sm')]: {
                width: '100%',
                height: 47,
                backgroundColor: 'white',
                border: '1px solid #cccccc',
                color: '#333333',
                marginTop: 1.4 + 'rem',
            },
        },
        mobileContainer: {
            display: 'flex',
            width: '100%',
            height: 47,
            justifyContent: 'space-between',
        },
        tagsFilterWrap: {
            width: 370,
            marginRight: 30,

            [theme.breakpoints.down('xs')]: {
                width: '45%',
                marginRight: 0,
            },
        },
    });

export default injectSheet(styles)(CoursesFilter);
